import { Locale } from "src/sanity/types"
import { approvalRatePerIndustryCode } from "./approvalRatePerIndustryCode"
import { CVRInfo, getCVRInfo } from "./cvr"
import { cvrIndustryCodeToFcpRiskScore } from "./fcpRiskScore"
import { cvrInfoToHubspotCompany, InitialSalesOutcome, InitialSalesOutcomeMap, updateCompany } from "./hubspot"
import { longIdNamesToShort } from "./oneclick/util"
import { ArpuEstimation } from "./arpuModel"
import Config from "src/Config"

export async function sendIntercomRequest(
    path: string,
    method: "GET" | "POST" | "PUT" | "DELETE",
    body?: any,
    retries = 1,
) {
    const isACreatingRequest = (method === "POST" || method === "PUT") && !path.includes("search")
    const options: RequestInit = {
        method,
        signal: AbortSignal.timeout(isACreatingRequest ? 7500 : 3500),
        ...(body ? { body: JSON.stringify(body) } : {}),
        headers: {
            accept: "application/json",
            "Intercom-Version": "2.14",
            "content-type": "application/json",
            authorization: "Bearer dG9rOjk1ZDMwODdkX2RjNzJfNGMxZV85N2U4X2ViZThjYjUxYWZkNToxOjA=",
        },
    }
    try {
        // console.log("Making request to Intercom", method, path)
        const response = await fetch("https://api.intercom.io" + path, options)
        // console.log("Intercom response status", response.status)
        if (response.status === 202) return { sucess: true }

        if (response.status !== 200 && response.status !== 404) {
            throw new Error(`Intercom API returned a non-200 response: ${response.status} ${response.statusText}`)
        }
        const responseBody = await response.json()
        let starting_after = responseBody?.pages?.next?.starting_after
        if (starting_after && responseBody?.pages?.page === 1 && responseBody?.pages?.total_pages > 1) {
            console.log(
                "Intercom response has",
                responseBody.pages.total_pages,
                "pages and",
                responseBody.total_count,
                "results.",
            )
        }
        while (starting_after && !body.pagination?.starting_after) {
            const nextPageResponseBody = await sendIntercomRequest(path, method, {
                ...body,
                pagination: { ...body?.pagination, starting_after },
            })
            responseBody.data.push(...nextPageResponseBody.data)
            starting_after = nextPageResponseBody?.pages?.next?.starting_after
        }
        if (responseBody.errors) throw new Error(JSON.stringify(responseBody.errors))

        // console.log("Sucessfully made request to Intercom", method, path)
        return responseBody
    } catch (error) {
        // We don't retry on creating requests, as they might be non-idempotent
        if (retries > 0 && error.name === "TimeoutError" && !isACreatingRequest) {
            console.log(`Retrying... (${retries} attempts left)`)
            return sendIntercomRequest(path, method, body, retries - 1)
        }

        if (error?.message?.includes("User Not Found")) {
            console.warn("Warning in intercomSendRequest:", method, path, error?.message)
        } else {
            console.error("Error in intercomSendRequest:", method, path, error)
        }
        return { data: [] }
    }
}

export async function searchIntercomContacts(body: IntercomSearchRequest): Promise<IntercomUser[]> {
    const response = await sendIntercomRequest("/contacts/search", "POST", body)
    return response.data
}

interface createLeadInIntercomParams {
    segment: string
    phone: string
    email: string
    leadSource: LeadSource
    cvr?: number
    referrer?: string
    utm_source?: string
    utm_campaign?: string
    lang?: Locale
    ids?: Record<string, string>
    customAttributes?: IntercomUserCustomAttributes
    deviceType?: string
    name?: string
    marketingConsent?: boolean
    intercomLeadId?: string
    forceCreateNewLead?: boolean
}
export interface createLeadInIntercomResponse {
    success: boolean
    intercomLeadId?: string
    message?: string
    error?: string
    arpuEstimate?: ArpuEstimation["arpuClassEstimate"]
}
export async function createLeadInIntercom({
    segment,
    phone,
    email,
    cvr,
    leadSource,
    referrer,
    utm_source,
    utm_campaign,
    lang,
    ids,
    customAttributes,
    deviceType,
    name,
    marketingConsent,
    intercomLeadId,
    forceCreateNewLead = false,
}: createLeadInIntercomParams): Promise<createLeadInIntercomResponse> {
    // Check if lead already exists with same phone, email, or cvr
    const intercomSegment: IntercomUser["custom_attributes"]["lead_segment"] =
        segment === "private" ? "personal" : "business"
    const isNewContact = !intercomLeadId
    const oneMonthsAgoInSeconds = Math.floor((new Date().getTime() - 30 * 24 * 60 * 60 * 1000) / 1000)
    const existingContacts =
        isNewContact && !forceCreateNewLead
            ? await getExistingContacts({
                  phone,
                  email,
                  cvr,
                  segment: intercomSegment,
                  lead_date_at: oneMonthsAgoInSeconds,
              })
            : []
    lang = lang || (isNewContact ? "dk" : null) // Default to "dk" if not provided, but only for new contacts

    if (existingContacts.length > 0) {
        // Don't create lead in intercom if it already exists
        const orderedLeads = existingContacts.sort((a, b) => a.created_at - b.created_at)
        const firstLead = orderedLeads[0]

        // We allow "new-cvr" leads to be overwritten, if it has not been worked on yet
        if (existingContacts.length === 1 && firstLead.custom_attributes["lead_source"] === "new-cvr") {
            const initialSalesOutcome = firstLead.custom_attributes["hubspot_initial_sales_outcome"]
            const outcomesThatWeLetOverride: (InitialSalesOutcome | undefined)[] = [
                undefined,
                InitialSalesOutcomeMap["Assigned"],
                InitialSalesOutcomeMap["Irrelevant"],
                InitialSalesOutcomeMap["ExpiredLead"],
            ]
            // If the only existing lead is from "new-cvr" source, and the initial sales outcome is not set or is "Assigned" or "Irrelevant", we allow updating the lead source
            if (outcomesThatWeLetOverride.includes(initialSalesOutcome)) {
                // Temp log as error, to monitor this new feature
                console.log(
                    `Overwriting lead source of existing Intercom contact ${firstLead.id} (${firstLead.role}, boarding_started: ${firstLead.custom_attributes["boarding_started"]}, boarding_applied: ${firstLead.custom_attributes["boarding_applied"]}) from '${firstLead.custom_attributes["lead_source"]}' to '${leadSource}'`,
                )

                // Mark Hubspot company irrelevant
                if (firstLead.custom_attributes["HubSpot company ID"]) {
                    try {
                        await updateCompany(firstLead.custom_attributes["HubSpot company ID"], {
                            initial_sales_outcome: InitialSalesOutcomeMap["Irrelevant"],
                            initial_sales_outcome_drill_down: "Irrelevant: Duplicate",
                        })
                        console.log("Marked Hubspot company as irrelevant", {
                            companyId: firstLead.custom_attributes["HubSpot company ID"],
                        })
                    } catch (e) {
                        console.error("Error marking Hubspot company as irrelevant", e)
                    }
                }

                // Archive Intercom lead
                await sendIntercomRequest(`/contacts/${firstLead.id}/archive`, "POST")
            }
        } else {
            console.log(
                `Deduplicated lead from lead source '${leadSource}'. Matching existing Intercom contacts:`,
                existingContacts
                    .map((contact) => contact.id + " (" + contact?.custom_attributes?.["lead_source"] + ")")
                    .join(", "),
            )

            if (leadSource === "application-dropoff") {
                console.log("Tag application-dropoff lead with event")
                await sendIntercomRequest("/events", "POST", {
                    event_name: "application dropoff",
                    created_at: Math.floor(new Date().getTime() / 1000),
                    id: firstLead.id,
                })
            } else {
                await sendIntercomRequest("/contacts/" + firstLead.id, "PUT", {
                    custom_attributes: {
                        latest_deduplicated_lead_source: leadSource,
                        ...(cvr?.toString()?.length === 8 && !firstLead.custom_attributes?.["CVR nummer"]
                            ? { "CVR nummer": cvr }
                            : {}),
                    },
                })
                await sendIntercomRequest("/events", "POST", {
                    event_name: "deduplicated lead",
                    created_at: Math.floor(new Date().getTime() / 1000),
                    id: firstLead.id,
                    metadata: {
                        lead_source: leadSource,
                        cvr,
                        ...(referrer ? { "Website lead signup page": referrer } : {}),
                    },
                })
            }
            return { success: true, intercomLeadId: firstLead.id, message: "Lead already exists" }
        }
    }

    // Validate required fields: must have either phone, email, or intercomLeadId AND leadSource or intercomLeadId
    const hasIdentification = Boolean(phone || email || cvr || intercomLeadId)
    const hasLeadSourceOrId = Boolean(leadSource || intercomLeadId)

    if (!hasIdentification || !hasLeadSourceOrId) {
        console.error("intercom:CreateLead missing required fields", {
            identification: { phone, email, intercomLeadId },
            leadSource,
        })
        return { success: false, message: "Missing required fields" }
    }

    // Enrich lead with external data
    let cvrInfo: Partial<CVRInfo>
    let arpuEstimate: ArpuEstimation
    if (cvr && cvr.toString().length === 8) {
        ;[cvrInfo, arpuEstimate] = await Promise.all([
            getCVRInfo(cvr),
            (async () => {
                const arpu = await fetch(Config.BASE_URL + `/api/getArpuEstimate?cvr=${cvr}`, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }).then((res) => res.json())
                return arpu
            })(),
        ])
    }

    let idsParam
    if (ids) {
        const shortNameIds = longIdNamesToShort(ids)
        idsParam = encodeURIComponent(new URLSearchParams(shortNameIds).toString())
    }

    // Intercom lead:
    const intercomPayload: IntercomUser = {
        ...(isNewContact ? { role: "lead" } : {}),
        ...(email ? { email } : {}),
        ...(phone ? { phone: formatPhone(phone) } : {}),
        ...(name ? { name } : {}),
        ...(typeof marketingConsent !== "undefined" ? { unsubscribed_from_emails: !marketingConsent } : {}),
        ...(referrer ? { referrer } : {}),
        ...(cvrInfo?.name ? { name: cvrInfo?.name } : {}),
        ...(lang ? { language_override: localeToIntercomLang(lang) } : {}),
        custom_attributes: {
            ...(isNewContact
                ? {
                      "lead_date_at": Math.floor(new Date().getTime() / 1000),
                      "lead_segment": intercomSegment,
                      "lead_source": leadSource,
                  }
                : {}),
            ...(typeof cvr === "number" ? { "CVR nummer": cvr } : {}),
            ...(idsParam ? { "Website lead visit IDs": idsParam } : {}),
            ...(lang ? { "Preferred language": lang } : {}),
            // Get referrer url without query params
            ...(referrer ? { "Website lead signup page": referrer.split("?")[0] } : {}),
            ...(utm_source ? { "Website lead session source": utm_source } : {}),
            ...(utm_campaign ? { "Website lead session campaign": utm_campaign } : {}),
            ...(deviceType ? { "Lead device type": deviceType } : {}),
            ...(cvrInfo?.industryCode ? cvrInfoToCustomAttributes(cvrInfo as CVRInfo) : {}),
            ...(arpuEstimate
                ? {
                      arpu_tier_estimate: arpuEstimate.arpuTierEstimate,
                      arpu_class_estimate: arpuEstimate.arpuClassEstimate,
                      arpu_model_type: arpuEstimate.modelType,
                  }
                : {}),

            // Spread customAttributes from incoming request parameter into custom_attributes
            // filter out empty values in customAttributes
            ...(customAttributes
                ? Object.fromEntries(Object.entries(customAttributes).filter(([, value]) => !!value))
                : {}),
            ...(leadSource === "application-dropoff"
                ? { application_dropoff_at: Math.floor(new Date().getTime() / 1000) }
                : {}),
        },
    }

    try {
        const method = isNewContact ? "POST" : "PUT"
        const path = isNewContact ? "/contacts" : `/contacts/${intercomLeadId}`
        const contactRes = await sendIntercomRequest(path, method, intercomPayload)
        const newIntercomLeadId = contactRes.id
        console.log({ newIntercomLeadId })

        // Trigger the event "created lead" in Intercom – so that we can use it as an instant trigger in Intercom series
        if (isNewContact) {
            await Promise.all([
                sendIntercomRequest(`/contacts/${newIntercomLeadId}`, "PUT", {
                    custom_attributes: { "lead_id": newIntercomLeadId },
                }), // Update the lead with the new lead id
                sendIntercomRequest("/events", "POST", {
                    event_name: "created lead",
                    created_at: Math.floor(new Date().getTime() / 1000),
                    id: newIntercomLeadId,
                    metadata: {
                        lead_source: leadSource,
                        lead_id: newIntercomLeadId,
                        cvr,
                    },
                }),
            ])
        } else {
            await sendIntercomRequest("/events", "POST", {
                event_name: "updated lead",
                created_at: Math.floor(new Date().getTime() / 1000),
                id: intercomLeadId,
                metadata: {
                    lead_source: leadSource,
                    updated_fields: Object.entries(customAttributes)
                        .filter(([, value]) => Boolean(value))
                        .map(([key, value]) => `${key}=${value}`)
                        .join(","),
                },
            })
        }

        return {
            success: true,
            message: isNewContact ? "New lead created" : "Updated lead",
            intercomLeadId: newIntercomLeadId,
            arpuEstimate: arpuEstimate?.arpuClassEstimate,
        }
    } catch (e) {
        return { success: false, message: "Error: " + e?.message, error: e.message }
    }
}

function formatPhone(phone: string) {
    if (phone) {
        // Remove non-digits and non-plus
        phone = phone.replace(/[^0-9+]/g, "")
        // Replace 00 with countrycode
        if (phone.startsWith("00")) {
            phone = "+" + phone.slice(2)
        }

        // Danish numbers
        if (phone.length === 10 && phone.startsWith("45")) {
            phone = "+45" + phone.slice(2)
        }
        if (phone.length === 8 && !phone.startsWith("+")) {
            phone = "+45" + phone
        }
        // Swedish numbers
        if ((phone.length === 11 || phone.length === 12) && phone.startsWith("46")) {
            phone = "+46" + phone.slice(2)
        }
    }
    return phone
}

function localeToIntercomLang(lang: Locale) {
    switch (lang) {
        case "dk":
            return "da"
        case "en":
            return "en"
        case "se":
            return "sv"
        case "no":
            return "no"
        default:
            return lang
    }
}

export async function getExistingContacts({
    phone,
    email,
    cvr,
    role,
    segment,
    lead_date_at,
}: {
    phone?: string
    email?: string
    cvr?: number
    role?: "user" | "lead"
    segment?: IntercomUserCustomAttributes["lead_segment"]
    lead_date_at?: number
}): Promise<IntercomUser[]> {
    // Search conditions
    const conditions = [
        ...(email && email?.match(/^[^@]+@[^@]+\.[^@]{2,}$/) !== null
            ? [
                  {
                      field: "email",
                      operator: "~",
                      value: email,
                  },
              ]
            : []),
        ...(phone && ("" + phone).length > 3
            ? [
                  {
                      field: "phone",
                      operator: "=",
                      value: phone.replaceAll("++", "+"),
                  },
              ]
            : []),
        ...(cvr && ("" + cvr).length > 3 && cvr.toString().replaceAll("0", "") !== ""
            ? [
                  {
                      field: "custom_attributes.CVR nummer",
                      operator: "=",
                      value: cvr,
                  },
              ]
            : []),
        ...(cvr && ("" + cvr).length > 3 && cvr.toString().replaceAll("0", "") !== ""
            ? [
                  {
                      field: "custom_attributes.organisation_number",
                      operator: "=",
                      value: "" + cvr, // This field is of type string
                  },
              ]
            : []),
    ]

    // If no conditions, return empty array
    if (conditions.length === 0) return []

    const leads: { data: IntercomUser[] } = await sendIntercomRequest("/contacts/search", "POST", {
        query: {
            operator: "AND",
            value: [
                ...(lead_date_at
                    ? [
                          {
                              field: "custom_attributes.lead_date_at",
                              operator: ">",
                              value: lead_date_at,
                          },
                          {
                              field: "custom_attributes.lead_date_at",
                              operator: "<",
                              value: lead_date_at + 31 * 24 * 60 * 60, // 31 days in seconds (Matching the 30 days in the createLeadInIntercom function)
                          },
                      ]
                    : []),
                ...(role
                    ? [
                          {
                              field: "role",
                              operator: "=",
                              value: role,
                          },
                      ]
                    : []),
                ...(role === "user"
                    ? [
                          {
                              field: "custom_attributes.customer_state",
                              operator: "!=",
                              value: "closed",
                          },
                      ]
                    : []),
                ...(segment
                    ? [
                          {
                              // Different fields for users and leads
                              field:
                                  role === "user"
                                      ? "custom_attributes.customer_type"
                                      : "custom_attributes.lead_segment",
                              operator: "=",
                              value: segment,
                          },
                      ]
                    : []),
                {
                    operator: "OR",
                    value: conditions,
                },
            ],
        },
    })

    if (cvr && ("" + cvr).length > 3) {
        const leadsWithSameCVR = leads.data.filter((lead) => {
            const leadCvr = lead.custom_attributes["CVR nummer"]

            if (leadCvr === 0 || !leadCvr) return true // Allow match if lead has no CVR, or is 0 (e.g. "I don't have a CVR number")
            if (leadCvr?.toString() === cvr?.toString()) return true
            return false
        })
        return leadsWithSameCVR
    } else {
        return leads.data
    }
}

export function cvrInfoToCustomAttributes(cvrInfo: CVRInfo): Partial<IntercomUserCustomAttributes> {
    return {
        "CVR industry code": Number(cvrInfo?.industryCode),
        "CVR industry name": cvrInfo?.industryName,
        "CVR industry category": cvrInfo?.industryCategory,
        "CVR company name": cvrInfo?.name,
        "CVR company name - url encoded": encodeURIComponent(cvrInfo?.name),
        "CVR company type": cvrInfo?.companyType,
        "CVR company founded at": cvrInfo?.foundedDateTimestamp / 1000,
        "CVR company purpose": cvrInfo?.purpose,
        "CVR company type last change at": cvrInfo?.companyTypeLastChangeAt,
        "CVR industry last change at": cvrInfo?.companyIndustryLastChangeAt,
        "CVR marketing consent": cvrInfo?.marketingConsent,
        "FCP risk score": cvrIndustryCodeToFcpRiskScore[cvrInfo?.industryCode],
        "CVR industry approval rate": approvalRatePerIndustryCode[cvrInfo?.industryCode], // 0-100
    }
}

export async function syncIntercomContacts() {
    const oneDayAgoInSeconds = Math.floor((new Date().getTime() - 5 * 24 * 60 * 60 * 1000) / 1000)
    const leadsToSync: { data: IntercomUser[] } = await sendIntercomRequest("/contacts/search", "POST", {
        query: {
            operator: "AND",
            value: [
                {
                    field: "custom_attributes.CVR nummer",
                    operator: "!=",
                    value: null,
                },
                {
                    field: "custom_attributes.CVR nummer",
                    operator: "!=",
                    value: "0",
                },
                {
                    field: "custom_attributes.CVR industry name",
                    operator: "!=",
                    value: null,
                },
                // {
                //     field: "role",
                //     operator: "=",
                //     value: "lead",
                // },
                {
                    field: "custom_attributes.lead_date_at",
                    operator: ">",
                    value: oneDayAgoInSeconds,
                },
                {
                    field: "custom_attributes.Preferred language",
                    operator: "!=",
                    value: "se",
                },
            ],
        },
        "pagination": {
            "per_page": 150,
        },
    })
    console.log("Found leads to sync", leadsToSync.data.length)
    console.log("First lead", leadsToSync.data[0])

    // Update CVR info
    for await (const lead of leadsToSync.data) {
        const { custom_attributes } = lead
        const cvr = lead.custom_attributes["CVR nummer"]
        if (Number(cvr) > 0 && cvr?.toString()?.length === 8 && !custom_attributes?.["CVR industry code"]) {
            // Get CVR info
            const cvrInfo = await getCVRInfo(cvr)
            console.log({ cvrInfo })
            if (cvrInfo?.cvr) {
                const cvrInfoPayload = cvrInfoToCustomAttributes(cvrInfo as CVRInfo)
                console.log("Sending to Intercom", lead.id)
                await sendIntercomRequest(`/contacts/${lead.id}`, "PUT", { custom_attributes: cvrInfoPayload })

                if (custom_attributes["HubSpot company ID"]) {
                    console.log("Sending to hubspot")
                    await updateCompany(custom_attributes["HubSpot company ID"], cvrInfoToHubspotCompany(cvrInfo))
                    console.log("updated company", custom_attributes["HubSpot company ID"], cvrInfo.name)
                }
            }
        }
    }
    return "Updated" + leadsToSync.data.length
}
export interface IntercomUser {
    type?: string
    id?: string
    workspace_id?: string
    external_id?: string
    role?: "user" | "lead"
    email?: string
    phone?: string
    name?: string
    created_at?: number
    updated_at?: number
    signed_up_at?: number
    last_seen_at?: number
    language_override?: string
    location?: Location
    custom_attributes: IntercomUserCustomAttributes
    tags?: Tags
    unsubscribed_from_emails?: boolean
}

export interface Location {
    type: string
    country: string
    region: string
    city: string
    country_code: string
    continent_code: string
}

export const partnerNames = [
    "dinero",
    "dinero-100",
    "legaldesk",
    "billy",
    "flatpay",
    "pensopay",
    "dandomain",
    "theramp",
    "simplelaw",
    "tier3",
    "nyfoeretagarcentrum",
] as const
export type Partner = (typeof partnerNames)[number]
export type LeadSource =
    | "web-business-signup"
    | "web-business-contact-me"
    | "new-cvr"
    | "application-dropoff"
    | "web-business-switching"
    | `partner-${Partner}-get-started`
    | `partner-${Partner}-contact-me`
    | "partner-theramp-auxiliary-company"
    | "web-banking-services"
    | "web-sas-eurobonus"
    | "b2c-show-in-app-interest-in-b2b-contact-me"
    | "b2c-show-in-app-interest-in-b2b-get-started"
    | "welcoming-rejected"
    | "campaign"

export interface IntercomUserCustomAttributes {
    "application_dropoff_at"?: number
    "Ceased business"?: boolean
    "Dissolved after bankruptcy"?: boolean
    "Forced dissolvement"?: boolean
    "Preferred language"?: Locale
    "Under bankruptcy"?: boolean
    "Website lead signup page"?: string
    "Website lead session source"?: string
    "Website lead session campaign"?: string
    lead_date_at?: number
    lead_source?: LeadSource
    "latest_deduplicated_lead_source"?: string
    "organisation_number"?: number
    "CVR nummer"?: number
    "CVR industry code"?: number
    "CVR industry name"?: string
    "CVR industry category"?: string
    "CVR company name"?: string
    "CVR company name - url encoded"?: string
    "CVR company type"?: string
    "CVR company founded at"?: number
    "CVR company purpose"?: string
    "CVR company type last change at"?: number
    "CVR industry last change at"?: number
    "CVR marketing consent"?: boolean
    "company_name"?: string
    "FCP risk score"?: number
    "CVR industry approval rate"?: number
    "Houston phone consent"?: boolean
    "Houston marketing consent"?: boolean
    "Private account has NemKonto"?: boolean
    "Current private account plan"?: string
    "Other customer relations"?: string
    "Number of other active Lunar Business accounts"?: number
    "Lunar identity ID"?: string
    "Pipedrive deal ID"?: string
    "HubSpot company ID"?: string
    "sent_to_outbound_at"?: number
    customer_birthDate_at?: number
    customer_birthDate_str?: string
    customer_country?: "DK" | "SE" | "NO"
    customer_gender?: string
    customer_is_minor?: boolean
    customer_relation?: string
    customer_state?: string
    customer_type?: "business" | "personal"
    boarding_started_at?: number
    boarding_applied_at?: number
    boarding_approved_at?: number
    boarding_rejected_at?: number
    boarding_redo_at?: number
    boarding_rejection_reason?: string
    plan_tier?: string
    "Website lead experiments"?: string
    "Website lead visit IDs"?: string
    "lead_segment"?: "business" | "personal"
    "Referred by"?: string
    platform_application_started?: string
    lead_note?: string
    arpu_tier_estimate?: "Standard ARPU" | "High ARPU"
    arpu_class_estimate?: number
    arpu_model_type?: string
    lead_sent_to_company_registration_at?: number
    company_registration_started_at?: number
    company_registration_submitted_at?: number
    company_registration_successful_at?: number
    company_registration_failed_at?: number
    company_registration_status?:
        | "sent_to_partner"
        | "application_started"
        | "application_submitted"
        | "registration_successful"
        | "registration_failed"
    lead_personalized_message_nudge_1?: string
    lead_personalized_subject_nudge_1?: string
    hubspot_initial_sales_outcome?: InitialSalesOutcome
    hubspot_number_of_deals_created?: number
    hubspot_deal_created_at?: number
}

export interface Tags {
    type: string
    data: any[]
    url: string
    total_count: number
    has_more: boolean
}

// All top-level searchable fields in IntercomUser
type IntercomUserPrimitiveFields = {
    [K in keyof IntercomUser]: IntercomUser[K] extends Record<string, unknown> ? never : K
}[keyof IntercomUser]

// All custom attributes
type IntercomUserCustomAttributeFields = {
    [K in keyof IntercomUserCustomAttributes]: `custom_attributes.${K & string}`
}[keyof IntercomUserCustomAttributes]

// Union of all allowed query fields
type IntercomSearchField = IntercomUserPrimitiveFields | IntercomUserCustomAttributeFields

// Operators supported by Intercom search API
type IntercomSearchOperator = "=" | "!=" | ">" | "<" | "~" | "!~" | "IN" | "NIN"

// A single condition
interface IntercomSearchCondition {
    field: IntercomSearchField
    operator: IntercomSearchOperator
    value: string | number | boolean | null | (string | number | boolean | null)[]
}

// Recursive AND/OR query
interface IntercomSearchQuery {
    operator: "AND" | "OR"
    value: (IntercomSearchCondition | IntercomSearchQuery)[]
}

// Pagination type
interface IntercomPagination {
    per_page?: number
    starting_after?: string
}

// Request body
interface IntercomSearchRequest {
    query: IntercomSearchQuery
    pagination?: IntercomPagination
}
